import {
  Collapse,
  Divider,
  Group,
  ScrollArea,
  Stack,
  Text,
} from "@mantine/core"
import { Card, Pagination } from "@tfg/ui"
import { useEffect, useMemo, useState, useRef, useCallback, FC } from "react"
import { useTranslation } from "react-i18next"

import { FindMappingByTypeResponseDto } from "~/gen/api/types"
import { CardHeaderTitle } from "~/pages/Admin/Mapping/components/CardHeaderTitle"
import { EditableSelect } from "~/pages/Admin/Mapping/components/EditableSelect"
import { MappingSelect } from "~/pages/Admin/Mapping/components/MappingSelect"
import { SearchBox } from "~/pages/Admin/Mapping/components/SearchBox"
import {
  MappingCollapseProps,
  MappingTypeNames,
  Pagination as PaginationType,
  TypeKeys,
  TypeMapping,
} from "~/pages/Admin/Mapping/types"

type Props = MappingCollapseProps & {
  typesList: TypeKeys[]
  types: FindMappingByTypeResponseDto["mappings"]
  typeName: MappingTypeNames
  pagination: PaginationType
  search: string
  setSearch: (search: string) => void
}

export type typeComponent = {
  isNew: boolean
  type: TypeMapping
}

export const MappingTable: FC<Props> = ({
  typesList,
  types,
  typeName,
  pagination,
  search,
  isOpen,
  close,
  open,
  setSearch,
}) => {
  const { t } = useTranslation(["organizationSettings", "common"])

  const typeComponents = useMemo(() => {
    return types
      .sort((a, b) => (a.typeId === null ? -1 : b.typeId === null ? 1 : 0))
      .map((type) => {
        return {
          isNew: false,
          type,
        }
      })
  }, [types])

  const [typeComponent, setTypeComponent] =
    useState<typeComponent[]>(typeComponents)

  const scrollRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setTypeComponent(typeComponents)
  }, [typeComponents])

  const translation =
    typeName === "PRODUCT"
      ? t("organizationSettings:mapping.product")
      : typeName == "MATERIAL"
        ? t("organizationSettings:mapping.material")
        : t("organizationSettings:mapping.standards")

  const handleCreate = useCallback(() => {
    if (typeComponent.filter((component) => component.isNew).length > 0) return
    const type = {
      isNew: true,
      type: {
        alias: "",
        id: "0",
        type: MappingTypeNames.PRODUCT,
        typeId: 0,
        key: "",
      },
    }
    setTypeComponent([...typeComponent, type])

    setTimeout(() => {
      scrollRef.current?.scrollIntoView({ behavior: "smooth" })
    }, 0)
  }, [typeComponent])

  const handleCancel = useCallback(
    (id: string) => {
      const newTypes = typeComponent.filter((type) => type.type.id !== id)
      setTypeComponent(newTypes)
    },
    [typeComponent]
  )

  const tableContent = useMemo(() => {
    if (typeComponent.length === 0)
      return (
        <Group justify="center">
          <Text>{t("common:lists.noData.title")}</Text>
        </Group>
      )
    return typeComponent.map((type, index) => {
      return (
        <Stack
          key={type.type.id}
          ref={index === typeComponent.length - 1 ? scrollRef : undefined}
          px="var(--tfg-organism-padding-x)"
          py="var(--tfg-organism-padding-y)"
        >
          {type.isNew ? (
            <EditableSelect
              typesList={typesList}
              mappingType={type}
              typeName={typeName}
              handleCancel={() => handleCancel(type.type.id)}
              page={pagination.currentPage}
            />
          ) : (
            <MappingSelect
              typesList={typesList}
              mappingType={type}
              typeName={typeName}
              page={pagination.currentPage}
            />
          )}
        </Stack>
      )
    })
  }, [
    typeComponent,
    handleCancel,
    pagination.currentPage,
    typeName,
    typesList,
    t,
  ])

  return (
    <Card>
      <Card.Header>
        <CardHeaderTitle
          titlePrefix={translation}
          isCollapseOpened={isOpen}
          openCollapse={open}
          closeCollapse={close}
          isOpen={isOpen}
          handleCreate={handleCreate}
        />
        <Divider />
      </Card.Header>
      <Collapse in={isOpen} transitionDuration={0} animateOpacity={false}>
        <Card.Section>
          <SearchBox
            search={search}
            onChange={(value) => {
              pagination.onPageChange(1)
              setSearch(value.toString())
            }}
          />
          <ScrollArea mah="50vh" style={{ overflowY: "scroll" }}>
            {tableContent}
          </ScrollArea>
          <Group justify="center">
            <Pagination
              total={pagination.totalPages}
              value={pagination.currentPage}
              onChange={pagination.onPageChange}
            />
          </Group>
        </Card.Section>
      </Collapse>
    </Card>
  )
}
